import { useState, useRef } from "react";
import DataGrid from "react-data-grid";
import { gql, useQuery } from "@apollo/client";
import _ from "lodash";
import moment from "moment";
import { IoIosSearch } from "react-icons/io";
import { RightModal } from "../layout/rightModal";
import Spinner from "../misc/Spinner";
import BookingSettings from "./BookingSettings";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const formatDate = (date) =>
  date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });

export const formatTime = (date) =>
  date.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

export const formatCost = (cost) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(cost);

export const timeDiffInMins = (start, end) => {
  const differenceInMilliseconds = start.getTime() - end.getTime();
  const differenceInMins = differenceInMilliseconds / (1000 * 60);
  return differenceInMins;
};

export const formatDuration = (durationInMins) => {
  if (durationInMins === 30) return "30m";

  const hours = Math.floor(durationInMins / 60);
  const minutes = durationInMins % 60;

  if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

const BOOKINGS_QUERY = gql`
  query GetBookingsByStatus($active: Boolean!, $limit: Int) {
    getBookingsByStatus(active: $active, limit: $limit) {
      confirmation {
        charged_amount
      }
      connector_id
      createdAt
      end_date
      evse_id
      evse_name
      id
      location_id
      location_name
      location_address
      start_date
      time_zone
      user_id
      user_email
      user_name
    }
  }
`;

var columns = [
  {
    key: "date",
    headerCellClass: "brandPrimaryColor",
    name: "Date",
    resizable: true,
  },
  {
    key: "start_time",
    headerCellClass: "brandPrimaryColor",
    name: "Start Time",
    resizable: true,
  },
  {
    key: "end_time",
    headerCellClass: "brandPrimaryColor",
    name: "End Time",
    resizable: true,
  },
  {
    key: "duration",
    headerCellClass: "brandPrimaryColor",
    name: "Duration",
    resizable: true,
  },
  {
    key: "cost",
    headerCellClass: "brandPrimaryColor",
    name: "Cost",
    resizable: true,
  },
  {
    key: "location_name",
    headerCellClass: "brandPrimaryColor",
    name: "Location",
    resizable: true,
  },
  {
    key: "evse_id",
    headerCellClass: "brandPrimaryColor",
    name: "EVSE ID",
    resizable: true,
  },
  {
    key: "connector_id",
    headerCellClass: "brandPrimaryColor",
    name: "Port",
    resizable: true,
  },
  {
    key: "user_name",
    headerCellClass: "brandPrimaryColor",
    name: "User",
    resizable: true,
  },
  {
    key: "user_email",
    headerCellClass: "brandPrimaryColor",
    name: "Email",
    resizable: true,
  },
  {
    key: "time_zone",
    headerCellClass: "brandPrimaryColor",
    name: "Time Zone",
    resizable: true,
  },
  {
    key: "createdAt",
    headerCellClass: "brandPrimaryColor",
    name: "Created on",
    resizable: true,
  },
];

export const BookingsManager = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [bookingStatus, setBookingStatus] = useState("ACTIVE");
  const searchInputRef = useRef();

  const showActiveBookings = bookingStatus === "ACTIVE";
  const bookingsResult = useQuery(BOOKINGS_QUERY, {
    variables: { active: showActiveBookings, limit: 1000 },
    fetchPolicy: showActiveBookings ? "no-cache" : undefined,
  });

  if (bookingsResult.loading) return <Spinner />;

  if (bookingsResult.error) return <p>Error</p>;

  const bookings = bookingsResult.data?.getBookingsByStatus || [];

  const parsedSearchQuery = searchQuery.toLowerCase().trim();
  const searchResults = bookings.filter(
    (b) =>
      (b.location_name || "").toLowerCase().includes(parsedSearchQuery) ||
      (b.evse_id || "").toLowerCase().includes(parsedSearchQuery) ||
      (b.user_name || "").toLowerCase().includes(parsedSearchQuery) ||
      (b.user_email || "").toLowerCase().includes(parsedSearchQuery)
  );

  const gridData = searchResults.map((booking) => {
    const startDate = new Date(booking.start_date);
    const endDate = new Date(booking.end_date);
    const createdDate = new Date(booking.createdAt);

    return {
      date: moment(startDate).format("DD-MMM-YY"),
      start_time: formatTime(startDate),
      end_time: formatTime(endDate),
      duration: formatDuration(timeDiffInMins(endDate, startDate)),
      cost:
        booking.confirmation.charged_amount > 0
          ? formatCost(booking.confirmation.charged_amount)
          : "$FREE",
      location_name: booking.location_name,
      evse_id: booking.evse_id,
      connector_id: booking.connector_id,
      user_name: booking.user_name,
      user_email: booking.user_email,
      time_zone: booking.time_zone,
      createdAt: createdDate.toLocaleString("en-AU"),
    };
  });

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="light"
      />
      <div className="flex flex-row">
        <div className="flex flex-col  ml-1">
          <h1 className="heading text-3xl font-bold mt-1">
            Booking Manager ({searchResults.length})
          </h1>
          <p className="text-gray-500 ml-1">
            This page is exclusive to EVUp Charge Adminstrators
          </p>
        </div>

        <div className="flex flex-row ml-auto max-h-11">
          <RightModal title="Booking Settings">
            <BookingSettings />
          </RightModal>
        </div>
      </div>
      <hr className="my-2 border border-gray-400" />
      <div className="flex">
        <div className="flex-column">
          <div style={{ width: "22.5rem" }}>
            <div className="relative flex w-full flex-wrap items-stretch">
              <input
                ref={searchInputRef}
                type="search"
                className="relative m-0 block flex-auto rounded border border-solid border-neutral-300 bg-transparent bg-clip-padding pl-3 pr-1 py-[0.25rem] text-base font-normal leading-[1.6] text-neutral-700 outline-none transition duration-200 ease-in-out focus:z-[3] focus:border-primary focus:text-neutral-700 focus:shadow-[inset_0_0_0_1px_#2fdce1] focus:outline-none dark:border-neutral-600 dark:text-neutral-200 dark:placeholder:text-neutral-200 dark:focus:border-primary"
                placeholder="Search by location/evse/user/email"
                aria-label="Search"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <IoIosSearch className="brandPrimaryColor mt-1" size={30} />
            </div>
          </div>
        </div>
        <div className="flex-column">
          <div
            style={{ height: 36 }}
            className="ml-5 flex flex-row bg-primary brandPrimaryColor p-2 rounded"
          >
            <label className="mr-2 my-auto">Status:</label>
            <select
              className="border rounded"
              name="booking-status"
              value={bookingStatus}
              onChange={(e) => {
                setBookingStatus(e.target.value);
                setSearchQuery("");
              }}
            >
              <option value="ACTIVE" label="Active" />
              <option value="PAST" label="Past" />
            </select>
          </div>
        </div>
      </div>
      <div className="mt-2">
        <style>
          {
            ".rdg { height: 100vh !important; max-height: calc( 100vh - 158px ) }"
          }
        </style>
        <DataGrid columns={columns} rows={gridData} className="rdg-dark" />
      </div>
    </div>
  );
};
